import { ReactNode } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Package, FileText, Upload, CheckCircle, AlertCircle, XCircle, Loader2 } from "lucide-react";
import { OrderInfoHeader } from "./OrderInfoHeader";
import { WorkflowSteps } from "./WorkflowSteps";
import { StatusSummaryBar } from "./StatusSummaryBar";
import { useWorkstationStore } from "@/hooks/useWorkstationStore";
import { cn } from "@/lib/utils";

interface WorkstationLayoutProps {
  children: ReactNode;
  orderId: string;
  orderInfo: {
    supplierName: string;
    deliveryDate: string;
    status: string;
  };
}

export const WorkstationLayout = ({ children, orderId, orderInfo }: WorkstationLayoutProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const { 
    activeView, 
    setActiveView, 
    getProductStats,
    isLoading,
    isSubmitting,
    isProcessingAI
  } = useWorkstationStore();

  const stats = getProductStats();
  const currentView = (searchParams.get('view') as 'scan' | 'invoice' | 'submit') || 'scan';

  // Sync URL with store
  if (currentView !== activeView) {
    setActiveView(currentView);
  }

  const handleViewChange = (view: 'scan' | 'invoice' | 'submit') => {
    setActiveView(view);
    setSearchParams({ view });
  };

  const handleBackNavigation = () => {
    navigate('/');
  };

  const viewConfig = {
    scan: {
      icon: Package,
      label: 'Incoming Scan',
      description: 'Scan & photo products',
      color: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800',
      activeColor: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700'
    },
    invoice: {
      icon: FileText,
      label: 'Invoice Matching',
      description: 'Process invoices & match',
      color: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800',
      activeColor: 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900 dark:text-purple-200 dark:border-purple-700'
    },
    submit: {
      icon: Upload,
      label: 'Submission Console',
      description: 'Review & sync to Square',
      color: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800',
      activeColor: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-200 dark:border-green-700'
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Enhanced Header */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 space-y-4">
          {/* Back Navigation */}
          <div>
            <Button 
              variant="ghost" 
              onClick={handleBackNavigation}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Orders
            </Button>
          </div>

          {/* Page Title */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Workstation</h1>
              <p className="text-muted-foreground">Process incoming order products</p>
            </div>

            {/* Status Indicators */}
            <div className="flex items-center gap-2">
              {isProcessingAI && (
                <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  AI Processing
                </Badge>
              )}
              {isSubmitting && (
                <Badge variant="secondary" className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Submitting
                </Badge>
              )}
              {isLoading && (
                <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Loading
                </Badge>
              )}
            </div>
          </div>

          {/* Order Information Card */}
          <OrderInfoHeader 
            supplierName={orderInfo.supplierName}
            orderId={orderId}
            deliveryDate={orderInfo.deliveryDate}
            status={orderInfo.status}
          />

          {/* Workflow Steps */}
          <WorkflowSteps 
            activeView={currentView}
            onViewChange={handleViewChange}
            completionPercentage={stats.total > 0 ? Math.round((stats.ready / stats.total) * 100) : 0}
          />

          {/* Status Summary */}
          <StatusSummaryBar 
            stats={stats}
          />
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
};